import React, { useState, useEffect } from "react";
import { FaBars, FaChevronDown, FaXmark } from "react-icons/fa6";
import { Link, NavLink, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import ChangeLanguageDropdown from "../../commons/Language/Language";

// Every page of the site renders this header, so the site stylesheets load here:
// template base first, then the mobile-first layer that overrides it.
import "../assets/css/main.css";
import "../ConstructionHome.css";

const LEISTUNGEN = [
  { to: "/leistungen/wärmedämmverbundsystem", key: "leistungenTitles.title1" },
  { to: "/leistungen/trockenbau", key: "leistungenTitles.title2" },
  { to: "/leistungen/baumontage", key: "leistungenTitles.title3" },
  { to: "/leistungen/verputz", key: "leistungenTitles.title4" },
  { to: "/leistungen/treppenhausreinigung", key: "leistungenTitles.title5" },
  { to: "/leistungen/hausmeisterservice", key: "leistungenTitles.title6" },
  { to: "/leistungen/gartenpflege", key: "leistungenTitles.title7" },
  { to: "/leistungen/winterdienst", key: "leistungenTitles.title8" },
  { to: "/leistungen/weitereleistungen", key: "leistungenTitles.title9" }
];

const DESKTOP_QUERY = "(min-width: 1200px)";

const Header = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const [subOpen, setSubOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);

  const isLeistungenActive = location.pathname.startsWith("/leistungen");

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 100);
    handleScroll();
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Close the menu after navigating.
  useEffect(() => {
    setMenuOpen(false);
    setSubOpen(false);
  }, [location.pathname]);

  // While the phone menu is open: lock page scroll, close on Escape or when the
  // viewport grows to the desktop layout.
  useEffect(() => {
    if (!menuOpen) return undefined;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = e => e.key === "Escape" && setMenuOpen(false);
    const mql = window.matchMedia(DESKTOP_QUERY);
    const onChange = e => e.matches && setMenuOpen(false);
    document.addEventListener("keydown", onKey);
    mql.addEventListener?.("change", onChange);
    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener("keydown", onKey);
      mql.removeEventListener?.("change", onChange);
    };
  }, [menuOpen]);

  return (
    <header
      id="header"
      className={`site-header fixed-top ${isScrolled ? "sticked" : ""} ${
        menuOpen ? "is-open" : ""
      }`}
    >
      <div className="container-xl site-header__bar">
        <Link to="/" className="site-header__logo">
          <span className="sitename">{t("UpConstruction")}</span>
          <span className="site-header__dot" aria-hidden="true">
            .
          </span>
        </Link>

        <button
          type="button"
          className="site-header__toggle"
          aria-expanded={menuOpen}
          aria-controls="navmenu"
          aria-label={menuOpen ? t("Close menu") : t("Open menu")}
          onClick={() => setMenuOpen(open => !open)}
        >
          {menuOpen ? <FaXmark aria-hidden="true" /> : <FaBars aria-hidden="true" />}
        </button>

        <nav id="navmenu" className="site-nav">
          <ul className="site-nav__list">
            <li>
              <NavLink to="/" exact className="site-nav__link">
                {t("headerTexts.title1")}
              </NavLink>
            </li>
            <li className={`site-nav__item--has-sub ${subOpen ? "is-open" : ""}`}>
              <button
                type="button"
                className={`site-nav__link site-nav__subtoggle ${
                  isLeistungenActive ? "active" : ""
                }`}
                aria-expanded={subOpen}
                aria-controls="navmenu-leistungen"
                onClick={() => setSubOpen(open => !open)}
              >
                {t("headerTexts.title2")}
                <FaChevronDown className="site-nav__chevron" aria-hidden="true" />
              </button>
              <ul id="navmenu-leistungen" className="site-nav__sub">
                {LEISTUNGEN.map(item => (
                  <li key={item.to}>
                    <NavLink to={item.to} className="site-nav__sublink">
                      {t(item.key)}
                    </NavLink>
                  </li>
                ))}
              </ul>
            </li>
            <li>
              <NavLink to="/references" className="site-nav__link">
                {t("headerTexts.title3")}
              </NavLink>
            </li>
            <li>
              <NavLink to="/partner" className="site-nav__link">
                {t("headerTexts.title4")}
              </NavLink>
            </li>
            <li>
              <NavLink to="/contacts" className="site-nav__link">
                {t("headerTexts.title5")}
              </NavLink>
            </li>
            <li>
              <NavLink to="/job" className="site-nav__link">
                {t("headerTexts.title6")}
              </NavLink>
            </li>
          </ul>

          <div className="site-nav__actions">
            <a className="site-header__login" href="/login">
              {t("Login")}
            </a>
            <ChangeLanguageDropdown />
          </div>
        </nav>
      </div>
    </header>
  );
};

export default Header;
